/**
 * GraphQL queries and hooks — technologyDomain pillar.
 * Split from graphql/queries.ts (issue #1409).
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';
import { useAllPages } from './useAllPages';
import type {
  TechnologyDomain,
  TechnologyDomainConnection,
  TechnologyDomainFilter,
} from '../types/technologyDomain';

/**
 * Fragment for TechnologyDomain fields
 */
export const TECHNOLOGY_DOMAIN_FIELDS = gql`
  fragment TechnologyDomainFields on TechnologyDomain {
    entityId
    entityType
    name
    description
    tags
    notes
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
    _deletedAt
  }
`;

/**
 * Query to get a single technology domain by ID
 */
export const GET_TECHNOLOGY_DOMAIN = gql`
  ${TECHNOLOGY_DOMAIN_FIELDS}
  query GetTechnologyDomain($id: ID!) {
    technologyDomain(id: $id) {
      ...TechnologyDomainFields
    }
  }
`;

/**
 * Query to list all technology domains with filtering and pagination
 */
export const LIST_TECHNOLOGY_DOMAINS = gql`
  ${TECHNOLOGY_DOMAIN_FIELDS}
  query ListTechnologyDomains($filter: TechnologyDomainFilter, $first: Int, $after: String) {
    technologyDomains(filter: $filter, first: $first, after: $after) {
      edges {
        node {
          ...TechnologyDomainFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

/**
 * Hook to get a single technology domain
 */
export function useTechnologyDomain(
  id: string
): QueryResult<{ technologyDomain: TechnologyDomain | null }> {
  return useQuery(GET_TECHNOLOGY_DOMAIN, {
    variables: { id },
    skip: !id,
  });
}

/**
 * Hook to list technology domains with optional filtering
 */
export function useTechnologyDomains(
  filter?: TechnologyDomainFilter,
  first: number = 50
): QueryResult<{ technologyDomains: TechnologyDomainConnection }> {
  return useQuery(LIST_TECHNOLOGY_DOMAINS, {
    variables: { filter, first },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

/**
 * Hook to list every technology domain (all pages, not capped at one server
 * page) — issue #764. Used to populate the Technology Domain filterable
 * picker on `TechnologyCapabilityForm`, which needs the full catalog to
 * search against rather than a single capped page. See {@link useAllPages}.
 */
export function useAllTechnologyDomains(
  filter?: TechnologyDomainFilter,
  first: number = 250
): QueryResult<{ technologyDomains: TechnologyDomainConnection }> {
  return useAllPages<{ technologyDomains: TechnologyDomainConnection }>(
    LIST_TECHNOLOGY_DOMAINS,
    (data) => data?.technologyDomains,
    { filter },
    first
  );
}
